import { Controller } from '@hotwired/stimulus';
import { say, esc } from '../lib/text';

// Type a few letters, pick one of the answers. The list comes from the host
// app — this engine knows nothing about what is being searched, only that
// the endpoint answers ?q= one of two ways:
//
//   JSON  [{ id, text, hint, url, img }]  drawn here, one button a row; img
//                                     (http(s) or relative) is a thumbnail
//   HTML  the list itself             shown as is; its [role=option] links
//                                     are the rows (qir docs/SEARCH.md)
//
// A form's typeahead: the visible input is what somebody types; the value
// that posts lives in a hidden field beside it, so a half-typed name never
// reaches the server as a selection. Pairs with AppFormBuilder#typeahead_input.
//
// The bar's (fire/shared/_search) is an ARIA combobox: the arrows walk the
// options and say which one (aria-activedescendant), the status line counts
// them, and a 429 or a 500 says so instead of closing as if nothing matched.
// A listbox holds options only, so every line it says (a miss, a flood) is a
// disabled option; when the host has shown its start panel, its doors stay
// under the line.
//
// Extra query params (scoping a city search to a nation, say) ride in
// paramsValue and are merged into every request.
export default class extends Controller {
  static targets = ['query', 'value', 'list', 'chosen', 'hotkey', 'status', 'spinner'];

  static values = {
    src: String,
    min: { type: Number, default: 2 },
    delay: { type: Number, default: 180 },
    params: { type: Object, default: {} },
    empty: String,
    // A pick that IS the action (the back office's "add to org"): the form
    // goes as soon as there is something real to send.
    submit: Boolean,
    // Ask with less than `min` typed too — the host answers its start panel.
    start: Boolean,
  };

  connect() {
    this.index = -1;
    this.rows = [];
    this.options = [];
    // A request that comes back after a newer one must not overwrite it.
    this.seq = 0;
    this.onAway = (e) => {
      if (!this.element.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onAway);
    // Tab to the next field is leaving too — the list must not hang open
    // over it looking like another input. A pick inside keeps focus here.
    this.onLeave = (e) => {
      if (!this.element.contains(e.relatedTarget)) this.close();
    };
    this.element.addEventListener('focusout', this.onLeave);
    this.armHotkey();
    this.armList();
  }

  disconnect() {
    document.removeEventListener('click', this.onAway);
    this.element.removeEventListener('focusout', this.onLeave);
    if (this.onHotkey) document.removeEventListener('keydown', this.onHotkey);
    clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
  }

  // The bar's box, not a form's: it says what it owns, form typeaheads don't.
  get combo() {
    return this.queryTarget.getAttribute('role') === 'combobox';
  }

  get listId() {
    if (!this.listTarget.id) this.listTarget.id = `typeahead-${Math.random().toString(36).slice(2, 8)}`;
    return this.listTarget.id;
  }

  // The rows are options, so the list is their listbox, named after the box it
  // answers. The bar's markup already says both; a form's list is drawn bare.
  armList() {
    const list = this.listTarget;
    // A list that scrolls is a tab stop once its rows are not (Chrome): never.
    list.tabIndex = -1;
    if (!list.hasAttribute('role')) list.setAttribute('role', 'listbox');
    if (list.hasAttribute('aria-label')) return;

    const box = this.queryTarget;
    list.setAttribute(
      'aria-label',
      box.labels?.[0]?.textContent.trim() || box.getAttribute('aria-label') || box.placeholder,
    );
  }

  // ⌘K / Ctrl+K focuses the bar's search. Form typeaheads have no hotkey
  // target, so they do not steal the chord from the box in the bar.
  armHotkey() {
    if (!this.hasHotkeyTarget) return;

    const mac = /Mac|iP(hone|ad|od)/.test(navigator.userAgent);
    this.hotkeyTarget.querySelectorAll('[data-mod]').forEach((el) => {
      el.hidden = (el.dataset.mod === 'mac') !== mac;
    });

    this.onHotkey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k' || e.repeat) return;
      e.preventDefault();
      this.queryTarget.focus();
      this.queryTarget.select();
    };
    document.addEventListener('keydown', this.onHotkey);
  }

  // Typing always clears the selection: what posts must be what is shown.
  input() {
    if (this.hasValueTarget) this.valueTarget.value = '';
    this.element.removeAttribute('data-chosen');
    clearTimeout(this.timer);

    const q = this.queryTarget.value.trim();
    if (q.length < this.minValue) return this.startValue ? this.search(q) : this.close();

    this.timer = setTimeout(() => this.search(q), this.delayValue);
  }

  async search(q) {
    const mine = ++this.seq;
    if (this.controller) this.controller.abort();
    // The start panel is the same every time: asked once a page.
    if (q.length < this.minValue && this.startHtml) return this.show(this.startHtml, false);

    this.controller = new AbortController();
    const url = new URL(this.srcValue, window.location.origin);
    url.searchParams.set('q', q);
    Object.entries(this.paramsValue).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, v);
    });

    this.busy(true);
    try {
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.controller.signal,
      });
      if (mine !== this.seq) return;
      if (!res.ok) return this.tell(say(res.status === 429 ? 'typeahead.busy' : 'typeahead.broken'));

      if ((res.headers.get('Content-Type') || '').includes('text/html')) {
        const html = await res.text();
        if (mine !== this.seq) return;
        if (q.length < this.minValue) this.startHtml = html;
        return this.show(html, q.length >= this.minValue);
      }

      const rows = await res.json();
      if (mine !== this.seq) return; // a newer keystroke already won
      this.rows = Array.isArray(rows) ? rows : [];
      this.render();
    } catch (e) {
      if (e.name !== 'AbortError' && mine === this.seq) this.tell(say('typeahead.broken'));
    } finally {
      if (mine === this.seq) this.busy(false);
    }
  }

  // The host's HTML is the list: its options are the rows.
  show(html, counted) {
    this.rows = null;
    this.listTarget.innerHTML = html;
    this.options = this.rowsOf();
    this.index = -1;
    this.count(counted ? this.options.filter((el) => !el.hasAttribute('data-foot')).length : null);
    this.open();
  }

  render() {
    this.index = -1;
    if (this.rows.length === 0) {
      this.listTarget.innerHTML = this.said(this.emptyValue || say('typeahead.empty'), 'text-base-content/70');
      this.options = [];
      this.count(0);
      return this.open();
    }

    this.listTarget.innerHTML = this.rows
      .map(
        (row, i) => `
        <li role="presentation">
          <button type="button" role="option" id="${this.listId}-${i}" aria-selected="false"
                  data-action="mousedown->typeahead#hold click->typeahead#choose" data-index="${i}"
                  class="w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex justify-between gap-2">
            <span>${esc(row.text)}</span>
            ${row.hint ? `<span class="text-base-content/70 shrink-0">${esc(row.hint)}</span>` : ''}
          </button>
        </li>`,
      )
      .join('');
    this.options = this.rowsOf();
    this.rows.forEach((row, i) => this.thumb(row, this.options[i]));
    this.count(this.rows.length);
    this.open();
  }

  // The row's picture, built as a node: the URL comes from the host's JSON and
  // never rides in a string. Only http(s) or a relative path becomes a src.
  thumb(row, option) {
    if (typeof row.img !== 'string' || !row.img || !option) return;

    let url;
    try {
      url = new URL(row.img, window.location.origin);
    } catch {
      return;
    }
    if (!/^https?:$/.test(url.protocol)) return;

    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    img.className = 'size-8 shrink-0 rounded object-cover';
    img.src = url.href;
    // A picture that will not load leaves the row as it was: text only.
    img.addEventListener('error', () => {
      img.remove();
      option.classList.remove('items-center');
      option.firstElementChild.classList.remove('grow');
    });
    option.classList.add('items-center');
    option.firstElementChild.classList.add('grow');
    option.prepend(img);
  }

  // What the list now holds, to walk with the arrows. The rows are never tab
  // stops: the box keeps the focus (aria-activedescendant), so Tab leaves it
  // and the list closes, and a page link the list half covers has no tabbable
  // neighbour to crowd it (2.5.8). A said line is no row.
  rowsOf() {
    const all = Array.from(this.listTarget.querySelectorAll('[role=option]'));
    all.forEach((el) => (el.tabIndex = -1));
    return all.filter((el) => el.getAttribute('aria-disabled') !== 'true');
  }

  // One line of words in the list: a disabled option, the only thing a
  // listbox may hold besides the rows.
  said(text, ink = '') {
    return `<li role="presentation"><div role="option" aria-disabled="true" aria-selected="false" class="px-3 py-3 text-sm ${ink}">${esc(text)}</div></li>`;
  }

  // A 429, a 500, the network gone: said in the list, never a silent close.
  // The host's start panel, if the box has shown it, stays under the line:
  // a search that is down still has doors.
  tell(text) {
    this.listTarget.innerHTML = this.said(text) + (this.startHtml || '');
    this.rows = this.startHtml ? null : [];
    this.options = this.rowsOf();
    this.index = -1;
    if (this.hasStatusTarget) this.statusTarget.textContent = text;
    this.open();
  }

  // "12 resultados" for a screen reader; nothing for the start panel.
  count(n) {
    if (!this.hasStatusTarget) return;
    if (n === null) return (this.statusTarget.textContent = '');

    const forms = say('typeahead.count') || {};
    const rule = new Intl.PluralRules(document.documentElement.lang || undefined).select(n);
    this.statusTarget.textContent = n
      ? String(forms[rule] || forms.other || n).replace('%{count}', n)
      : say('typeahead.empty');
  }

  busy(on) {
    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = !on;
  }

  // A press on a row keeps focus in the field, so focusout never closes the
  // list under the pick (Safari does not focus a clicked button).
  hold(event) {
    event.preventDefault();
  }

  choose(event) {
    this.pick(Number(event.currentTarget.dataset.index));
  }

  pick(i) {
    // The host's own rows are links: following one is the pick.
    if (this.rows === null) return this.options[i]?.click();

    const row = this.rows[i];
    if (!row) return;

    // A row that carries a url is a destination, not a value: the bar's
    // search picks a page, a form's picks an id. Same list, same keys.
    if (row.url) return window.location.assign(row.url);

    this.valueTarget.value = row.id;
    this.queryTarget.value = row.text;
    this.element.setAttribute('data-chosen', 'true');
    if (this.hasChosenTarget) this.chosenTarget.textContent = row.hint || '';
    this.close();
    // typeahead:picked — a neighbour that does more with the pick (the
    // address search: cep#place) listens; nobody listening costs nothing.
    this.dispatch('picked', { detail: row });
    if (this.submitValue && row.id) this.element.closest('form')?.requestSubmit();
  }

  // ↓ ↑ walk every option and wrap; Enter opens the one lit, or submits the
  // form (the results page) when none is; Esc closes, Esc again clears.
  keydown(event) {
    // A search input clears itself on Esc and says so with an input event,
    // which would open the start panel again: the box does it, quietly.
    if (event.key === 'Escape') {
      if (!this.combo) return this.close();
      event.preventDefault();
      if (!this.listTarget.hidden) return this.close();
      this.queryTarget.value = '';
      return;
    }
    if (this.listTarget.hidden || this.options.length === 0) return;

    const n = this.options.length;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.index = this.index < 0 && step < 0 ? n - 1 : (this.index + step + n) % n;
      this.highlight();
    } else if (event.key === 'Enter' && this.index >= 0) {
      event.preventDefault();
      this.pick(this.index);
    }
  }

  highlight() {
    const lit = this.rows === null ? 'menu-focus' : 'bg-base-200';
    this.options.forEach((el, i) => {
      el.setAttribute('aria-selected', String(i === this.index));
      el.classList.toggle(lit, i === this.index);
    });
    const el = this.options[this.index];
    if (!el) return;

    el.scrollIntoView({ block: 'nearest' });
    if (this.combo) this.queryTarget.setAttribute('aria-activedescendant', el.id);
  }

  // Only while the field still has the focus: an answer that lands after
  // the person tabbed on must not pop a list over the next field.
  open() {
    if (!this.element.contains(document.activeElement)) return;
    this.listTarget.hidden = false;
    if (this.combo) this.queryTarget.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.listTarget.hidden = true;
    this.index = -1;
    if (!this.combo) return;

    this.queryTarget.setAttribute('aria-expanded', 'false');
    this.queryTarget.removeAttribute('aria-activedescendant');
  }
}
